import { useField } from "formik";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { formatMessage } from "../../translations/format-message";
import FormikFormErrorHandler from "./FormikFormErrorHandler";

const CustomToolbar: React.FC<{toolbarId: string}> = ({ toolbarId }) => {
  return (
  
  <div id={toolbarId}>
    <select
      className="ql-font"
      defaultValue="arial"
      title={formatMessage("Font Family")}
    >
      <option value="arial">Arial</option>
      <option value="comic-sans">Comic Sans</option>
      <option value="courier-new">Courier New</option>
      <option value="georgia">Georgia</option>
      <option value="helvetica">Helvetica</option>
      <option value="lucida">Lucida</option>
    </select>
    <select
      className="ql-size"
      defaultValue="16px"
      title={formatMessage("Font Size")}
    >
      <option value="10px">10px</option>
      <option value="12px">12px</option>
      <option value="14px">14px</option>
      <option value="16px">16px</option>
      <option value="18px">18px</option>
      <option value="20px">20px</option>
      <option value="24px">24px</option>
      <option value="30px">30px</option>
    </select>
    <button className="ql-bold" title={formatMessage("Bold")} />
    <button className="ql-italic" title={formatMessage("Italic")} />
    <button className="ql-underline" title={formatMessage("Underline")} />
    <select className="ql-align" title={formatMessage("Underline")} />
    <button className="ql-strike" title={formatMessage("Strike")} />
    <select className="ql-color" title={formatMessage("Font Color")} />
    <select
      className="ql-background"
      title={formatMessage("Background Color")}
    />
    <button
      className="ql-script"
      value="sub"
      title={formatMessage("SubScript")}
    />
    <button
      className="ql-script"
      value="super"
      title={formatMessage("SuperScript")}
    />
    {/* <button className="ql-header" value="1" />
    <button className="ql-header" value="2" /> */}
    <button className="ql-blockquote" title={formatMessage("Blockquote")} />
    <button className="ql-code-block" title={formatMessage("Code Block")} />
    <button
      className="ql-list"
      value="ordered"
      title={formatMessage("Ordered List")}
    />
    <button
      className="ql-list"
      value="bullet"
      title={formatMessage("Bullet List")}
    />
    <button
      className="ql-indent"
      value="-1"
      title={formatMessage("Indentation")}
    />
    <button
      className="ql-indent"
      value="+1"
      title={formatMessage("Indentation")}
    />
    <button className="ql-link" title={formatMessage("Link")} />
    <button className="ql-image" title={formatMessage("Image")} />
    <button className="ql-video" title={formatMessage("Video")} />
    <button className="ql-formula" title={formatMessage("Formula")} />
    <button className="ql-clean" title={formatMessage("Clean All Formating")} />
  </div>
);
}

const FormikReactQuill: React.FC<any> = (props) => {
  const { label, ...rest } = props;
  const [field, meta, helpers] = useField(props);
  const [editorHtml, setEditorHtml] = useState(field.value || "");
  const quillRef = useRef<ReactQuill>(null);
  const toolbarId = `toolbar-${field.name}`;

  useEffect(() => {
    const Font = Quill.import("formats/font");
    Font.whitelist = [
      "arial",
      "comic-sans",
      "courier-new",
      "georgia",
      "helvetica",
      "lucida",
    ];
    Quill.register(Font, true);

    const Size = Quill.import("attributors/style/size");
    Size.whitelist = [
      "10px",
      "12px",
      "14px",
      "16px",
      "18px",
      "20px",
      "24px",
      "30px",
    ];
    Quill.register(Size, true);
  }, []);

  const handleBackspace = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection();
    if (range) {
      if (range.length > 0) {
        editor.deleteText(range.index, range.length);
      } else if (range.index > 0) {
        editor.deleteText(range.index - 1, 1);
        editor.setSelection(range.index - 1, 1);
      }
    }
  };

  const handleChange = (html: string) => {
    setEditorHtml(html);
    helpers.setValue(html);
  };

  const modules = {
    toolbar: !props.disabled
      ? {
          container: `#${toolbarId}`,
        }
      : false,
  };

  const formats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "script",
    "font",
    "size",
    "color",
    "background",

    "header",
    "blockquote",
    "code-block",
    "indent",
    "list",
    "align",
    "direction",

    "link",
    "image",
    "video",
    "formula",

    // Custom formats
    "clean",
  ];

  const styles = `
    /* Font styles */
    .ql-font-arial { font-family: Arial, sans-serif; }
    .ql-font-comic-sans { font-family: "Comic Sans MS", cursive, sans-serif; }
    .ql-font-courier-new { font-family: "Courier New", monospace; }
    .ql-font-georgia { font-family: Georgia, serif; }
    .ql-font-helvetica { font-family: Helvetica, sans-serif; }
    .ql-font-lucida { font-family: "Lucida Sans Unicode", "Lucida Grande", sans-serif; }
    
    /* Size styles */
    .ql-size-10px { font-size: 10px !important; }
    .ql-size-12px { font-size: 12px !important; }
    .ql-size-14px { font-size: 14px !important; }
    .ql-size-16px { font-size: 16px !important; }
    .ql-size-18px { font-size: 18px !important; }
    .ql-size-20px { font-size: 20px !important; }
    .ql-size-24px { font-size: 24px !important; }
    .ql-size-30px { font-size: 30px !important; }
    
    /* Additional styling for other formats */
    .ql-script[value="sub"] { font-variant-position: sub; }
    .ql-script[value="super"] { font-variant-position: super; }
    .ql-header-1 { font-size: 2em; }
    .ql-header-2 { font-size: 1.5em; }
    .ql-code-block { background: #f5f5f5; font-family: monospace; }
  `;

  return (
    <div className="text-editor absolute">
      <style>{styles}</style>
      {label && <label htmlFor={field.name}>{label}</label>}
      {
          !props.disabled &&
         <CustomToolbar toolbarId={toolbarId}/>
      }
      <ReactQuill
        // ref={quillRef}
        value={field.value || ""}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        theme="snow"
        readOnly={props.disabled}
        // {...rest}
      />
      <FormikFormErrorHandler intl={props.intl} meta={meta} />
    </div>
  );
};

export default injectIntl(FormikReactQuill);
